import React, { useEffect } from 'react';
import proptypes from 'prop-types';
import {
  arrayOf,
  bool,
  customType,
  namedFragment,
  params,
  shape,
  string,
  typename,
  useDataModel
} from '@thd-nucleus/data-sources';
import { useImpression } from '@thd-olt-component-react/impression';
import { ThemeProvider } from '@one-thd/sui-atomic-components';
import { getThemeByName } from '@thd-olt-component-react/stencil-themes';
import { BannerRenderer } from './BannerRenderer';
import { withImpressions } from '../hocs/withImpressions';
import { normalizeSlotData } from './utils/normalizeSlotData';

const { bool: boolean, number: numberType, string: stringType } = proptypes;

const MedioInline = withImpressions(({
  componentClass = '',
  componentId,
  slotContentId: slotName,
  mediaCollectionIndex = 0,
  componentPosition = 1,
  enableB2CRadius = false,
  lazyLoad = false,
  theme = undefined
}) => {

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('medio-inline.ready');
  }, []);

  // Legacy dataModel call - skip if slotName is provided (new fusion pattern)
  const { data: componentData, loading: componentLoading, error: componentError } = useDataModel('component', {
    variables: {
      componentClass,
      id: componentId || ''
    },
    skip: !!slotName // Skip if slotName exists (new pattern takes priority)
  });

  const { data: slotData, loading: slotLoading, error: slotError } = useDataModel('slot', {
    fetchPolicy: 'cache-only',
    variables: { id: slotName },
    skip: !slotName, // Backwards compatibility: skip if no slotName provided
  });

  // TODO: Determine which ID to use for slotId
  const impressionId = componentId || slotData?.slot?.entryId || '';

  const { ref, clickID } = useImpression({
    data: {
      id: impressionId,
      name: MedioInline.displayName,
      component: MedioInline.displayName,
      position: componentPosition,
      type: 'content'
    }
  });

  // Handle loading and errors
  if (componentLoading || componentError || slotLoading || slotError) {
    return null;
  }

  const normalizedData = normalizeSlotData({
    slotData,
    componentData,
    mediaCollectionIndex
  });

  if (!normalizedData) {
    return null;
  }

  const { desktopSourceData, mobileSourceData } = normalizedData;

  return (
    <ThemeProvider theme={getThemeByName(theme)}>
      <div
        // eslint-disable-next-line react/no-unknown-property
        clickid={clickID}
        ref={ref}
        data-component-id={impressionId}
        data-component-name="MedioInline"
        data-component-position={componentPosition}
        data-component="MedioInline"
        id={`${componentClass}-${componentPosition}-${impressionId}`}
      >
        <BannerRenderer
          data={normalizedData}
          mobileAspectRatio="sui-aspect-[640/241]"
          tabletAspectRatio="md:sui-aspect-[142/15]"
          desktopAspectRatio="xl:sui-aspect-[142/15]"
          lazyLoad={lazyLoad}
          componentId={impressionId}
          componentPosition={componentPosition}
          enableB2CRadius={enableB2CRadius}
          desktopSourceData={desktopSourceData}
          mobileSourceData={mobileSourceData}
        />
      </div>
    </ThemeProvider>
  );
});

MedioInline.propTypes = {
  componentClass: stringType,
  componentId: stringType,
  slotContentId: stringType,
  mediaCollectionIndex: numberType,
  componentPosition: numberType,
  enableB2CRadius: boolean,
  lazyLoad: boolean,
  theme: stringType
};

MedioInline.displayName = 'MedioInline';

// Do not touch
const DamMediaFragment = namedFragment({
  inline: false,
  fragmentType: 'DamMedia',
  fragmentAlias: 'DamMediaV1'
}).shape({
  damContentSelector: shape({
    assetData: arrayOf(
      shape({
        selectedImageUrl: string()
      })
    )
  }),
  damDownloadedContent: shape({
    url: string()
  }),
  id: string(),
  imageHotspot: shape({
    id: string(),
    componentName: string()
  }),
  __typename: typename('DamMedia')
});

MedioInline.dataModel = {
  component: params({
    id: string().isRequired(),
    componentClass: customType('ComponentClass').enum(['MedioInline']).isRequired()
  }).shape({
    MedioInline: namedFragment({
      inline: true,
      fragmentType: 'MedioInline'
    }).shape({
      componentName: string(),
      id: string(),
      link: string(),
      altText: string(),
      desktopImage: DamMediaFragment,
      mobileImage: DamMediaFragment,
      tabletImage: DamMediaFragment,
      theme: string()
    })
  }),
  slot: params({
    id: string().isRequired(),
  }).shape({
    id: string(),
    entryId: string(),
    mediaCollections: arrayOf(
      shape({
        entryId: string(),
        theme: string(),
        media: arrayOf(
          shape({
            entryId: string(),
            theme: string(),
            sourceDesktop: arrayOf(
              shape({
                entryId: string(),
                type: string(),
                uri: string(),
                hyperlink: string(),
                altText: string(),
                hotspot: string(),
              }),
            ),
            sourceMobile: arrayOf(
              shape({
                entryId: string(),
                type: string(),
                uri: string(),
                hyperlink: string(),
                altText: string(),
                hotspot: string(),
              }),
            ),
          }),
        ),
      }),
    ),
    __typename: typename('newSlot'),
  }),
};

export { MedioInline };
